import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import i18next from 'i18next'
import { z } from 'zod'

import { withinRunes } from '~/pages/AdminOcctoMaster/tabs/_common/schema'

/**
 * 連絡先エントリ入力 schema。
 *
 * 全 4 フィールド (department / name / phone / email) が任意 (spec AC-T04
 * / AC-03: proto TradingPartnerContactEntryInput の department / phone
 * min_len 撤去と整合)。 email は proto 側で入力時のみ RFC 5321 形式を
 * 検証するため、 frontend でも同じ制約をミラーして drift を防ぐ (= account.code の
 * max_len ミラーと同方針)。 空文字は「未入力」として許容し、 Create/Edit modal の
 * submit 時に undefined へ正規化する。
 */
const contactEntryInputSchema = z
  .object({
    department: z
      .string()
      .refine(withinRunes(30), '連絡先名称は30文字以内で入力してください'),
    name: z
      .string()
      .refine(withinRunes(30), '担当者は30文字以内で入力してください')
      .optional(),
    phone: z
      .string()
      .refine(withinRunes(15), '電話番号は15文字以内で入力してください'),
    email: z
      .string()
      .email('メールアドレスの形式が正しくありません')
      .refine(withinRunes(254), 'メールは254文字以内で入力してください')
      .or(z.literal(''))
      .optional(),
  })
  .refine(
    (v) =>
      v.department !== '' ||
      (v.name !== undefined && v.name !== '') ||
      v.phone !== '' ||
      (v.email !== undefined && v.email !== ''),
    {
      message: '連絡先の項目を少なくとも1つ入力してください',
    },
  )

export type ContactEntryInputValues = z.infer<typeof contactEntryInputSchema>

/**
 * OCCTO コード体系 (domain-knowledge.md §12) のエリア suffix マッピング。
 * AreaCode enum 値 → コード末尾1桁文字。
 */
const areaCodeToOcctoSuffix: Partial<Record<AreaCode, string>> = {
  [AreaCode.HOKKAIDO]: '1',
  [AreaCode.TOHOKU]: '2',
  [AreaCode.TOKYO]: '3',
  [AreaCode.CHUBU]: '4',
  [AreaCode.HOKURIKU]: '5',
  [AreaCode.KANSAI]: '6',
  [AreaCode.CHUGOKU]: '7',
  [AreaCode.SHIKOKU]: '8',
  [AreaCode.KYUSHU]: '9',
  [AreaCode.OKINAWA]: '0',
}

/**
 * 取引先コードの形式: 取引先コード欄には相手方の BG コード / 計画提出者コード
 * そのものを登録するが、 相手の区分によって入る値の種類 (= 2 文字目の体系) が
 * 変わる (spec AC-OMT-013)。 需要 BG が相手なら需要 BG コード (`L`+英字)、
 * 発電 BG または需要抑制契約者が相手なら計画提出者コード (`G`・`D`+数字)。
 * BG コードと計画提出者コードは 2 文字目の体系が異なる (OCCTO「BGコード、
 * 計画提出者コードの採番領域切替に関する注意喚起」2020-09) ため単一の
 * [GLD]+英字 体系ではバリデーションできない。 3〜4 文字目 (英数字、 I・O を
 * 除外) と末尾 1 桁数字 (エリアコード) は共通。 発電 BG が相手の場合に発電 BG
 * コード (`G`+英字) を入れるのは誤りで、 計画提出者コード (`G`+数字) を
 * 入れる (OCCTO「BP 標準記載要領」、 #7516、 #6279 の是正)。
 * backend entity (= NewTradingPartnerAccount の tradingPartnerAccountCodePattern)
 * と同一の正規表現。 共有 VO の valueobject.SubmissionEntityCode は 2〜4 文字目
 * すべて英数字を許す permissive な体系だが、 取引先登録の境界では上記の体系を
 * 必須にする (受理値は VO の部分集合)。
 */
const TRADING_PARTNER_CODE_FORMAT = /^(L[A-HJ-NP-Z]|[GD]\d)[A-HJ-NP-Z0-9]{2}\d$/

const CROSS_MASTER_DUPLICATE_MESSAGE =
  'このコードは既に登録されています。別のコードを入力してください'

// spec AC-OMT-013: 未入力 → 5 文字超 → 形式不正 の優先順で 1 つだけ
// message を出す (= superRefine + 早期 return で複数 issue の同時発火を
// 防ぐ。 backend entity の 3 チェック順序と揃える)。
// retainedCodes (保存前から保持しているコード) のうち 5 文字のものは、 旧形式でも
// 形式チェックを免除する (spec AC-OMT-013 の注記、 #8539。 backend の RetainTradingPartnerAccount と同じ)。
const buildAccountCodeSchema = (retainedCodes: Set<string>): z.ZodString =>
  z.string().superRefine((v, ctx) => {
    if (v.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '取引先コードを入力してください',
      })
      return
    }
    if (v.length > 5) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '取引先コードは5文字以内で入力してください',
      })
      return
    }
    const retained = v.length === 5 && retainedCodes.has(v)
    if (!TRADING_PARTNER_CODE_FORMAT.test(v) && !retained) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          '需要 BG が相手なら「L + 英字 + 英数字2文字 + エリアコード」で入力してください。発電 BG・需要抑制契約者なら「G・D + 数字 + 英数字2文字 + エリアコード」です。英字・英数字に I・O は使えません (例: LDDD3 / G8882 / D1002)',
      })
    }
  })

/**
 * アカウント入力の base schema (cross-field refine 前)。
 * D2: license を削除し area_code / code / code_name のみ必須。
 * spec AC-OMT-010（trading-partner-representative-flag Design Note）:
 * 同一取引先×同一エリアへの複数コード登録を許容する（一意性キーは area から
 * code へ移行済み。 area 軸の一意性制約は撤廃済み — 撤廃箇所は
 * buildTradingPartnerFormSchema 側の within-partner code 重複 superRefine を
 * 参照）。
 */
const accountInputBaseSchema = z.object({
  areaCode: z
    .nativeEnum(AreaCode)
    .refine((v) => v !== AreaCode.UNSPECIFIED, 'エリアを選択してください'),
  code: buildAccountCodeSchema(new Set()),
  codeName: z
    .string()
    .refine(
      withinRunes(150),
      '取引先コード名称は150文字以内で入力してください',
    ),
  // spec AC-OMT-030: 新規コードの代表フラグ既定 true。各コードは独立に
  // 代表になれる（排他選択にしない）。
  isRepresentative: z.boolean(),
  weekdayContacts: z.array(contactEntryInputSchema),
  holidayContacts: z.array(contactEntryInputSchema),
})

type AccountInputBaseSchema = typeof accountInputBaseSchema

const withAccountCrossFieldRules = (
  schema: AccountInputBaseSchema,
): AccountInputBaseSchema =>
  schema
    .refine(
      (v) => {
        if (v.code === '') return true
        const expected = areaCodeToOcctoSuffix[v.areaCode]
        if (expected === undefined) return true
        return v.code.endsWith(expected)
      },
      {
        message:
          '取引先コードの末尾をエリアコードにしてください (例: 東京は「3」)',
        path: ['code'],
      },
    )
    // spec AC-OMT-004/029: 連絡先を保持できるのは代表アカウントのみ
    // (backend entity NewTradingPartnerAccount の不変条件と同じ、client 側
    // parity)。 AccountSection は非代表時に連絡先セクションを非表示にし
    // 代表解除時に連絡先を空へ揃えるため通常到達しないが、防御的に固定する。
    .refine((v) => v.isRepresentative || v.weekdayContacts.length === 0, {
      message: '非代表のアカウントは連絡先を登録できません',
      path: ['weekdayContacts'],
    })
    .refine((v) => v.isRepresentative || v.holidayContacts.length === 0, {
      message: '非代表のアカウントは連絡先を登録できません',
      path: ['holidayContacts'],
    })

const buildAccountInputObjectSchema = (
  retainedCodes: Set<string>,
): AccountInputBaseSchema =>
  withAccountCrossFieldRules(
    accountInputBaseSchema.extend({
      code: buildAccountCodeSchema(retainedCodes),
    }),
  )

const accountInputObjectSchema = withAccountCrossFieldRules(
  accountInputBaseSchema,
)

export type AccountInputValues = z.infer<typeof accountInputObjectSchema>
export type AccountInputInput = z.input<typeof accountInputObjectSchema>

/**
 * アカウント入力 schema を組み立てる。
 *
 * existingBgCodes に含まれる code は BG マスタと重複するため reject する。
 * Create / Edit モーダルは list query から set を渡し、テストは空 set を使う。
 * Edit では編集対象の取引先が既に保持しているコードを set から除外する。
 */
const buildAccountInputSchema = (
  existingBgCodes: Set<string>,
  retainedCodes: Set<string>,
): z.ZodType<AccountInputValues, AccountInputInput> =>
  buildAccountInputObjectSchema(retainedCodes).refine(
    (v) => !(v.code !== '' && existingBgCodes.has(v.code)),
    {
      message: CROSS_MASTER_DUPLICATE_MESSAGE,
      path: ['code'],
    },
  )

/**
 * 取引先マスタの Create / Edit 共通 Zod schema の値型。
 *
 * BgMaster と同様、 Create / Edit で単一 schema を共有することで、 form 本体
 * (TradingPartnerFormFields / AccountSection) を両モードで再利用する。
 * D1: tradingPartnerCode を削除 (partner 単位コードは制度上不在、surrogate uuid で識別)。
 * D3: notificationRequired を削除 (契約属性で取引先の責務外)。
 */
export type TradingPartnerFormValues = {
  name: string
  shortName: string
  accounts: AccountInputValues[]
}

// Zod v4 では .refine の narrowing で z.input ≠ z.output になる (= UNSPECIFIED を
// input では許容するが output では除外)。 useForm の defaultValues / Control は
// narrowing 前の Input 型を受けるため、 submit handler だけが output (= narrow) を受け取る。
export type TradingPartnerFormInput = {
  name: string
  shortName: string
  accounts: AccountInputInput[]
}

/**
 * 取引先マスタの Create / Edit 共通 Zod schema を組み立てる。
 *
 * existingBgCodes に含まれる code は BG マスタと重複するため reject する。
 * Create / Edit モーダルは list query から set を渡し、テストは空 set を使う。
 * Edit では編集対象の取引先が既に保持しているコードを set から除外する。
 *
 * retainedCodes は Edit で編集対象の取引先が保存前から保持しているコード。
 * この set のコードは取引先コード形式チェックを免除する (spec AC-OMT-013 の注記)。
 */
export const buildTradingPartnerFormSchema = (
  existingBgCodes: Set<string> = new Set(),
  retainedCodes: Set<string> = new Set(),
): z.ZodType<TradingPartnerFormValues, TradingPartnerFormInput> =>
  z
    .object({
      name: z
        .string()
        .min(1, '名称を入力してください')
        .refine(withinRunes(100), '名称は100文字以内で入力してください'),
      shortName: z
        .string()
        .min(1, '略称を入力してください')
        .refine(withinRunes(30), '略称は30文字以内で入力してください'),
      // spec line 156 verbatim (0 件エラー)。schema はモジュール scope で
      // 組み立てるため React hook 経由の t は受け取れず、`i18next.t` を直接
      // 呼ぶ (CLAUDE.md「文言は t() 経由」)。`error` を関数で渡すと parse 時に
      // 評価される (Zod v4 error map) ため、 schema 構築時 (= モジュール import
      // 時) の i18next 初期化前評価は起きない。
      accounts: z
        .array(buildAccountInputSchema(existingBgCodes, retainedCodes))
        .min(1, {
          error: () =>
            i18next.t(
              'page:adminOcctoMaster.tradingPartner.form.accountsRequired',
            ),
        }),
    })
    // spec §ビジネスルール（trading-partner-representative-flag Design
    // Note）: 同一取引先内でコードが一意（旧 AC-OMT-023 の area 一意性は撤廃
    // 済み、 area 軸を code 軸に置換）。 backend entity の
    // validateTradingPartnerAccounts が同じ不変条件を CodeInvalidArgument で
    // 拒否するが、 backend からの汎用「入力内容に誤りがあります…」文言では
    // 到達しないため、 frontend で code 重複を検知して明示的な文言を surface
    // する (backend 側は defense-in-depth として残す)。
    .superRefine((val, ctx) => {
      const seen = new Map<string, number>()
      for (let i = 0; i < val.accounts.length; i++) {
        const a = val.accounts[i]
        if (a === undefined || a.code === '') continue
        const first = seen.get(a.code)
        if (first === undefined) {
          seen.set(a.code, i)
          continue
        }
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'この取引先コードは既に登録されています。別のコードを入力してください',
          path: ['accounts', i, 'code'],
        })
      }
    })

/** 空の existingBgCodes を使うテスト・fallback 用 schema。 */
export const tradingPartnerFormSchema = buildTradingPartnerFormSchema()
